import { useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from 'react';
import { CircleX, FolderPlus, Layers3, Plus, Send, ShieldCheck, Tag, X } from 'lucide-react';
import { createGalleryPreview } from '../core/gallery';
import { serializeProjectDocument } from '../core/projectFile';
import { useSiteCopy } from '../content/SiteCopyContext';
import type { GalleryCollection, GalleryTag, PatternProject } from '../types';

export function PublishPatternModal({ project, pointsEnabled, skipReview = false, onClose, onSubmitted }: { project: PatternProject; pointsEnabled: boolean; skipReview?: boolean; onClose: () => void; onSubmitted: () => void }) {
  const copy = useSiteCopy();
  const [title, setTitle] = useState(project.name);
  const [description, setDescription] = useState('');
  const [catalog, setCatalog] = useState<GalleryTag[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [collections, setCollections] = useState<GalleryCollection[]>([]);
  const [collectionId, setCollectionId] = useState('');
  const [showCollectionCreator, setShowCollectionCreator] = useState(false);
  const [collectionTitle, setCollectionTitle] = useState('');
  const [collectionDescription, setCollectionDescription] = useState('');
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [customTagInput, setCustomTagInput] = useState('');
  const [tagError, setTagError] = useState('');
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    createGalleryPreview(project).then((value) => { if (active) setPreview(value); }).catch((reason) => { if (active) setError(reason.message); });
    return () => { active = false; };
  }, [project]);

  useEffect(() => {
    fetch('/api/gallery/tags', { credentials: 'include' }).then((response) => response.json()).then((data) => setCatalog(data.tags || [])).catch(() => setError('分类标签读取失败，请稍后再试'));
    fetch('/api/gallery/my-collections', { credentials: 'include' }).then((response) => response.json()).then((data) => setCollections(data.collections || [])).catch(() => {});
  }, []);

  async function createCollection() {
    if (!collectionTitle.trim()) return;
    setCreatingCollection(true);
    setError('');
    try {
      const response = await fetch('/api/gallery/collections', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: collectionTitle, description: collectionDescription })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || '合辑创建失败');
      setCollections((current) => [data.collection, ...current]);
      setCollectionId(data.collection.id);
      setCollectionTitle('');
      setCollectionDescription('');
      setShowCollectionCreator(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '合辑创建失败');
    } finally {
      setCreatingCollection(false);
    }
  }

  const selectedCatalogNames = useMemo(() => {
    const selected = new Set(tagIds);
    return catalog.filter((tag) => selected.has(tag.id)).map((tag) => tag.name);
  }, [catalog, tagIds]);

  function collectCustomTags(raw = '') {
    const selectedNames = new Set(selectedCatalogNames.map((name) => name.toLocaleLowerCase('zh-CN')));
    const next = [...customTags];
    const known = new Set(next.map((name) => name.toLocaleLowerCase('zh-CN')));
    for (const value of raw.split(/[,，\n]+/)) {
      const name = value.trim().replace(/^#+/, '').replace(/\s+/g, ' ');
      if (!name) continue;
      if (name.length > 12) throw new Error('每个自定义标签不能超过 12 个字');
      const key = name.toLocaleLowerCase('zh-CN');
      if (selectedNames.has(key) || known.has(key)) continue;
      if (tagIds.length + next.length >= 5) throw new Error('内容标签最多选择或自定义 5 个');
      known.add(key);
      next.push(name);
    }
    return next;
  }

  function commitCustomTags() {
    try {
      const next = collectCustomTags(customTagInput);
      setCustomTags(next);
      setCustomTagInput('');
      setTagError('');
    } catch (reason) {
      setTagError(reason instanceof Error ? reason.message : '自定义标签不正确');
    }
  }

  function handleCustomTagKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter' && event.key !== ',' && event.key !== '，') return;
    event.preventDefault();
    commitCustomTags();
  }

  function toggleCatalogTag(id: string) {
    setTagIds((current) => {
      if (current.includes(id)) {
        setTagError('');
        return current.filter((value) => value !== id);
      }
      if (current.length + customTags.length >= 5) {
        setTagError('内容标签最多选择或自定义 5 个');
        return current;
      }
      setTagError('');
      return [...current, id];
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!preview) return;
    setSubmitting(true);
    setError('');
    try {
      if (!categoryId) throw new Error('请选择一个作品分类');
      const finalCustomTags = collectCustomTags(customTagInput);
      const response = await fetch('/api/gallery', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description, categoryId, collectionId: collectionId || null, tagIds, tags: finalCustomTags, previewData: preview, document: serializeProjectDocument(project) })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || '提交失败');
      onSubmitted();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '提交失败，请稍后再试');
    } finally { setSubmitting(false); }
  }

  return <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="publish-modal" role="dialog" aria-modal="true" aria-labelledby="publish-title">
      <button className="modal-close icon-button" type="button" onClick={onClose} aria-label="关闭"><CircleX size={19} /></button>
      <div className="publish-preview">{preview ? <img src={preview} alt={`${project.name}图纸预览`} /> : <span className="gallery-loader" />}</div>
      <form className="publish-form" onSubmit={submit}>
        <p className="eyebrow">SUBMIT PATTERN</p><h2 id="publish-title">{copy('publish.title')}</h2>
        <p>{skipReview ? '当前账号已获投稿免审核权限，提交后会直接公开；服务器只保存图纸数据和压缩预览。' : copy('publish.intro')}</p>
        <div className="publish-project-meta"><strong>{project.width} × {project.height}</strong><span>{project.empty.reduce((sum, empty) => sum + (empty ? 0 : 1), 0).toLocaleString('zh-CN')} 颗</span></div>
        <label htmlFor="publish-name">{copy('publish.nameLabel')}</label><input id="publish-name" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={40} required />
        <label htmlFor="publish-description">{copy('publish.descriptionLabel')}</label><textarea id="publish-description" value={description} onChange={(event) => setDescription(event.target.value)} maxLength={240} placeholder={copy('publish.descriptionPlaceholder')} />
        <label htmlFor="publish-category" className="publish-tags-label"><span><Tag size={14} />作品分类</span><small>必选 1 个</small></label>
        <select id="publish-category" value={categoryId} onChange={(event) => setCategoryId(event.target.value)} required><option value="">请选择分类</option>{catalog.filter((tag) => tag.kind === 'category').map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}</select>
        <div className="publish-collection-head"><label htmlFor="publish-collection"><span><Layers3 size={14} />创作者合辑</span><small>可选，不加入也能投稿</small></label><button type="button" onClick={() => setShowCollectionCreator((value) => !value)}><FolderPlus size={15} />新建合辑</button></div>
        <select id="publish-collection" value={collectionId} onChange={(event) => setCollectionId(event.target.value)}><option value="">不加入合辑</option>{collections.filter((item) => item.status !== 'hidden').map((item) => <option key={item.id} value={item.id}>{item.title}（{item.patternCount}{item.reviewStatus === 'pending' ? ' · 待审核' : item.reviewStatus === 'rejected' ? ' · 已退回' : ''}）</option>)}</select>
        {showCollectionCreator && <div className="publish-collection-create">
          <input value={collectionTitle} onChange={(event) => setCollectionTitle(event.target.value)} maxLength={16} placeholder="合辑名称，最多 16 个字" aria-label="新合辑名称" />
          <textarea value={collectionDescription} onChange={(event) => setCollectionDescription(event.target.value)} maxLength={240} placeholder="简单介绍这个合辑（可选）" aria-label="新合辑介绍" />
          <div><button type="button" onClick={() => setShowCollectionCreator(false)}>取消</button><button type="button" disabled={!collectionTitle.trim() || creatingCollection} onClick={createCollection}>{creatingCollection ? '创建中…' : '创建并选中'}</button></div>
        </div>}
        <label className="publish-tags-label"><span><Tag size={14} />内容标签</span><small>{tagIds.length + customTags.length}/5 · 可选预设或自定义</small></label>
        <div className="publish-tag-options">{catalog.filter((tag) => tag.kind === 'tag').map((tag) => <button className={tagIds.includes(tag.id) ? 'active' : ''} key={tag.id} type="button" aria-pressed={tagIds.includes(tag.id)} onClick={() => toggleCatalogTag(tag.id)}>#{tag.name}</button>)}{!catalog.some((tag) => tag.kind === 'tag') && <small>后台暂未建立预设标签，可直接添加自定义标签。</small>}</div>
        <div className="publish-custom-tag-row">
          <input id="publish-custom-tag" value={customTagInput} onChange={(event) => { setCustomTagInput(event.target.value); setTagError(''); }} onKeyDown={handleCustomTagKeyDown} placeholder="输入自定义标签，按回车添加" aria-label="自定义内容标签" maxLength={64} disabled={tagIds.length + customTags.length >= 5} />
          <button type="button" onClick={commitCustomTags} disabled={!customTagInput.trim() || tagIds.length + customTags.length >= 5}><Plus size={16} />添加</button>
        </div>
        {!!customTags.length && <div className="publish-custom-tags" aria-label="已添加的自定义标签">{customTags.map((tag) => <span key={tag}>#{tag}<button type="button" onClick={() => { setCustomTags((current) => current.filter((value) => value !== tag)); setTagError(''); }} aria-label={`删除自定义标签 ${tag}`}><X size={13} /></button></span>)}</div>}
        {tagError && <p className="publish-tag-error" role="alert">{tagError}</p>}
        <div className="publish-safe-note"><ShieldCheck size={16} /><span>{copy(pointsEnabled ? 'publish.pointsNote' : 'publish.pointsDisabledNote')}</span></div>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="primary-button" type="submit" disabled={!preview || submitting}><Send size={17} />{submitting ? copy('publish.submittingButton') : skipReview ? '直接发布' : copy('publish.submitButton')}</button>
      </form>
    </section>
  </div>;
}
